import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Switch,
  Card,
  CardContent,
  Chip,
  Grid,
  Avatar,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import agent from '../api/axios';
import { formatDateDisplay } from '../utils/dateUtils';
import Tooltip from '@mui/material/Tooltip';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import { useSnackbar } from 'notistack';

interface BusinessDto {
  id?: string;
  name?: string;
  ownerEmail?: string;
  role?: string;
  fein?: string;
  stateTaxId?: string;
  streetAddress?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phoneNumber?: string;
  payFrequency?: string;
  endOfWeek?: string;
  subscriptionName?: string;
  subscriptionTerm?: string;
  subscriptionEndDate?: string;
  subscriptionStatus?: string;
  isActive?: boolean;
}

export default function Users() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<BusinessDto[]>([]);
  const [loading, setLoading] = useState(false);

  // impersonation dialog state
  const [impersonOpen, setImpersonOpen] = useState(false);
  const [impersonId, setImpersonId] = useState<string | null>(null);
  const [impersonName, setImpersonName] = useState<string | null>(null);
  const [impersonLoading, setImpersonLoading] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await agent.Business.getAll();
      setItems(data ?? []);
    } catch (err) {
      console.error('Failed to load businesses', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleToggleActive = async (business: BusinessDto) => {
    if (!business.id) return;

    try {
      const newActiveStatus = !business.isActive;
      await agent.Business.toggleActive(business.id, newActiveStatus);
      enqueueSnackbar(
        `User ${newActiveStatus ? 'activated' : 'deactivated'} successfully`,
        { variant: 'success' }
      );
      fetchItems();
    } catch (err) {
      console.error('Failed to toggle active status', err);
      enqueueSnackbar('Failed to update user status', { variant: 'error' });
    }
  };

  const openImpersonate = (b: BusinessDto) => {
    if (!b?.id) return;
    setImpersonId(b.id);
    setImpersonName(b.name ?? b.ownerEmail ?? 'this user');
    setImpersonOpen(true);
  };

  const handleConfirmImpersonate = async () => {
    if (!impersonId) return;
    setImpersonLoading(true);
    try {
      const resp = await agent.Auth.impersonate(impersonId);
      const token = resp?.token ?? resp;
      if (token) {
        localStorage.setItem('token', token);
        window.dispatchEvent(new Event('authChanged'));
        window.location.href = '/';
      } else {
        enqueueSnackbar('Impersonation failed: no token returned', {
          variant: 'error',
        });
      }
    } catch (err) {
      console.error('Impersonation failed', err);
      enqueueSnackbar('Impersonation failed', { variant: 'error' });
    } finally {
      setImpersonLoading(false);
      setImpersonOpen(false);
      setImpersonId(null);
      setImpersonName(null);
    }
  };

  const handleDelete = async (business: BusinessDto) => {
    if (!business.id) return;

    const confirmMessage = `Are you sure you want to delete ${
      business.name || business.ownerEmail
    }?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await agent.Business.delete(business.id);
      enqueueSnackbar('User deleted successfully', { variant: 'success' });
      fetchItems();
    } catch (err: any) {
      console.error('Delete failed', err);
      enqueueSnackbar(err.response?.data?.message || 'Failed to delete user', {
        variant: 'error',
      });
    }
  };

  const getInitials = (b: BusinessDto) => {
    if (b.name) {
      return b.name.charAt(0).toUpperCase();
    }
    if (b.ownerEmail) {
      return b.ownerEmail.charAt(0).toUpperCase();
    }
    return '?';
  };

  const getStatusInfo = (b: BusinessDto) => {
    if (b.role?.toLowerCase() === 'client') {
      return {
        label: b.subscriptionStatus || 'No Subscription',
        color: b.subscriptionStatus === 'active' ? 'success' : 'default',
      };
    }
    return {
      label: b.isActive ? 'Active' : 'Inactive',
      color: b.isActive ? 'success' : 'default',
    };
  };

  // Mobile Card View
  const renderMobileCards = () => (
    <Grid container spacing={2}>
      {items.map((b) => {
        const status = getStatusInfo(b);
        return (
          <Grid item xs={12} key={b.id ?? JSON.stringify(b)}>
            <Card
              sx={{
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: b.role === 'Admin' ? 'secondary.main' : 'primary.main',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(b)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={600} noWrap>
                      {b.name || 'Unnamed'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {b.ownerEmail}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={b.role}
                    size="small"
                    color={b.role === 'Admin' ? 'secondary' : 'primary'}
                    variant="outlined"
                  />
                </Box>

                {/* Subscription Info (for clients) */}
                {b.role?.toLowerCase() === 'client' && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Subscription
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {b.subscriptionName || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Plan
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {b.subscriptionTerm || '-'}
                        </Typography>
                      </Grid>
                      {b.subscriptionEndDate && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Ends
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDateDisplay(b.subscriptionEndDate)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                <Divider sx={{ my: 1.5 }} />

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={status.label}
                    size="small"
                    color={status.color as any}
                    variant="filled"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={b.isActive ? 'Deactivate' : 'Activate'}>
                      <Switch
                        checked={b.isActive || false}
                        onChange={() => handleToggleActive(b)}
                        color="success"
                        size="small"
                      />
                    </Tooltip>
                    <Tooltip title="Impersonate">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => openImpersonate(b)}
                      >
                        <PersonIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(b)}
                      >
                        <DeleteIcon sx={{ color: 'error.main' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  // Desktop Table View
  const renderDesktopTable = () => (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Owner Email</TableCell>
            <TableCell>Subscription</TableCell>
            <TableCell>Plan</TableCell>
            <TableCell>Ends</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((b) => (
            <TableRow key={b.id ?? JSON.stringify(b)}>
              <TableCell>{b.name}</TableCell>
              <TableCell>{b.ownerEmail}</TableCell>
              <TableCell>
                {b.role?.toLowerCase() === 'client'
                  ? b.subscriptionName || '-'
                  : '-'}
              </TableCell>
              <TableCell>
                {b.role?.toLowerCase() === 'client'
                  ? b.subscriptionTerm || '-'
                  : '-'}
              </TableCell>
              <TableCell>
                {b.role?.toLowerCase() === 'client' && b.subscriptionEndDate
                  ? formatDateDisplay(b.subscriptionEndDate)
                  : '-'}
              </TableCell>
              <TableCell>
                {b.role?.toLowerCase() === 'client'
                  ? b.subscriptionStatus || '-'
                  : b.isActive
                  ? 'Active'
                  : 'Inactive'}
              </TableCell>
              <TableCell>{b.role}</TableCell>
              <TableCell align="right">
                <Tooltip
                  title={b.isActive ? 'Deactivate User' : 'Activate User'}
                >
                  <Switch
                    checked={b.isActive || false}
                    onChange={() => handleToggleActive(b)}
                    color="success"
                    size="small"
                  />
                </Tooltip>
                <Tooltip title="Impersonate">
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => openImpersonate(b)}
                    aria-label={`impersonate ${b.name ?? b.ownerEmail}`}
                  >
                    <PersonIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete User">
                  <IconButton
                    size="small"
                    aria-label={`delete ${b.name ?? b.ownerEmail}`}
                    onClick={() => handleDelete(b)}
                  >
                    <DeleteIcon sx={{ color: 'error.main' }} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ maxWidth: 1100, m: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
          }}
        >
          <AdminPanelSettingsIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Manage Users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage all registered users
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No users found
          </Typography>
        </Paper>
      ) : isMobile ? (
        renderMobileCards()
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Users ({items.length})
          </Typography>
          {renderDesktopTable()}
        </Paper>
      )}

      {/* Impersonation confirmation dialog */}
      <Dialog
        open={impersonOpen}
        onClose={() => setImpersonOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Impersonate User</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to impersonate {impersonName ?? 'this user'}. This
            will sign you in as them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setImpersonOpen(false)}
            disabled={impersonLoading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmImpersonate}
            disabled={impersonLoading}
            variant="contained"
          >
            {impersonLoading ? 'Impersonating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
